import { Action } from 'redux';
import { call, put, select } from 'redux-saga/effects';
import { BasketActions, BasketActionTypes, BasketLoadingItem, LoadingItemTypes } from '../';
import { AppState, ErrorAction } from 'store';
import { api } from 'repositories/api';
import { ACTION_CREATOR_NAME, userHelper, analyticsHelper, errorToBasketErrorModel } from 'helpers';
import { removeBasketItemSaga } from './removeBasketItem';
import { BasketItemModel, ProductTypes, BasketDetailsModel } from 'models/Basket';
import { updateSofashieldQueueSaga } from './updateSofashield';
import { initBasketPageSaga } from './initBasketPage';
import { BasketSagaReturnType, callBasketSaga } from 'store/sagaHelpers';

export interface UpdateBasketItemQuantityAction extends Action<BasketActionTypes.UPDATE_BASKET_ITEM_QUANTITY> {
    itemProductCode: string;
    quantity: number;
}

export interface UpdateBasketItemQuantityActionStart extends Action<BasketActionTypes.UPDATE_BASKET_ITEM_QUANTITY_START> {
    loadingItem: BasketLoadingItem;
    quantity: number;
}

export interface UpdateBasketItemQuantityActionSuccess extends Action<BasketActionTypes.UPDATE_BASKET_ITEM_QUANTITY_SUCCESS> {
    loadingItem: BasketLoadingItem;
}

export interface UpdateBasketItemQuantityActionError extends ErrorAction<BasketActionTypes.UPDATE_BASKET_ITEM_QUANTITY_ERROR> {
    loadingItem: BasketLoadingItem;
}

export const updateBasketItemQuantity: (itemProductCode: string, quantity: number) => UpdateBasketItemQuantityAction = (itemProductCode: string, quantity: number) => ({
    type: BasketActionTypes.UPDATE_BASKET_ITEM_QUANTITY,
    itemProductCode: itemProductCode,
    quantity: quantity
});
updateBasketItemQuantity[ACTION_CREATOR_NAME] = 'updateBasketItemQuantity';

export type UpdateBasketItemQuantityFunc = (...args: Parameters<typeof updateBasketItemQuantity>) => void;

/** The `updateBasketItemQuantity` saga was splitted into two part:
 * 1. Concurrent part.
 *    Allows to initiate several items update processes at the same time (and display loaders for example);
 * 2. Sequential part.
 *    Places all calls to the queue since api cannot handle parallel calls for update of basket items.
 *    Items can be removed only by `entryNumber` for now and it updates for each item after any update from the items list.
 */
export function* updateBasketItemQuantitySaga({ itemProductCode, quantity }: UpdateBasketItemQuantityAction) {
    yield put<BasketActions>({
        type: BasketActionTypes.UPDATE_BASKET_ITEM_QUANTITY_START,
        loadingItem: { id: itemProductCode, loadingType: LoadingItemTypes.ChangeQuantity },
        quantity
    });
}

export function* updateBasketItemQuantityQueueSaga({ loadingItem, quantity }: UpdateBasketItemQuantityActionStart): BasketSagaReturnType {
    try {
        if (quantity === 0) {
            yield call(removeBasketItemSaga, {
                type: BasketActionTypes.REMOVE_BASKET_ITEM,
                itemProductCode: loadingItem.id
            });
        } else {
            let state: AppState = yield select();
            let entryItem = state.basketState.basket!.entries?.find((entry: BasketItemModel) => entry.product.code === loadingItem.id);
            let entryNumber = entryItem?.entryNumber;
            const basketId = state.basketState.basket!.guid;

            if (entryNumber !== undefined) {
                yield api.hybris.updateBasketItemQuantity(userHelper.getCurrentUserName(), basketId, entryNumber, quantity);

                yield callBasketSaga(initBasketPageSaga, {
                    type: BasketActionTypes.INIT_BASKET_PAGE
                });

                state = yield select();
                sendAnalytics(entryItem!, entryItem!.quantity, quantity, state.basketState.basket!);

                if (entryItem!.sofashieldQuantity && entryItem!.sofashieldQuantity > 0) {
                    yield callBasketSaga(updateSofashieldQueueSaga, {
                        type: BasketActionTypes.UPDATE_SOFASHIELD_START,
                        loadingItem: { id: entryItem!.product.code, loadingType: LoadingItemTypes.ChangeSofashieldQuantity },
                        sofashieldQuantity: quantity
                    });
                }
            }
        }

        yield put<BasketActions>({
            type: BasketActionTypes.UPDATE_BASKET_ITEM_QUANTITY_SUCCESS,
            loadingItem
        });

        return true;
    } catch (error) {
        yield put<BasketActions>({
            type: BasketActionTypes.UPDATE_BASKET_ITEM_QUANTITY_ERROR,
            loadingItem,
            error: errorToBasketErrorModel(error, 'updateBasketItemQuantityQueueSaga')
        });

        return false;
    }
}


function sendAnalytics(item: BasketItemModel, prevQuantity: number, newQuantity: number, updatedBasket: BasketDetailsModel): void {
    analyticsHelper.withLogging(() => {
        const itemsCountDifference = newQuantity - prevQuantity;

        if (itemsCountDifference === 0) {
            return;
        }

        if (itemsCountDifference > 0) {
            const userId = userHelper.isUserLoggedIn() ? updatedBasket.session.userId : undefined;

            item.product.type === ProductTypes.DefaultProduct
                ? api.google.addDefaultProduct(item, Math.abs(itemsCountDifference), userId)
                : api.google.addAdditionalProduct(item.product.type as ProductTypes.FabricCareKit | ProductTypes.LeatherCareKit, item.product.code, Math.abs(itemsCountDifference));

            api.exponea.addToCart(updatedBasket, item, Math.abs(itemsCountDifference));
        } else {
            item.product.type === ProductTypes.DefaultProduct
                ? api.google.removeDefaultProduct(item, Math.abs(itemsCountDifference))
                : api.google.removeAdditionalProduct(item.product.type as ProductTypes.FabricCareKit | ProductTypes.LeatherCareKit, item.product.code, Math.abs(itemsCountDifference));

            api.exponea.removeFromCart(updatedBasket, item, Math.abs(itemsCountDifference));
        }
    }, 'updateBasketItemQuantityQueueSaga');
}
